//
//  KD在庫管理システム[375] ログイン画面
//
// ./LoginScreen.js
//


import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import '../styles/LoginScreen.css';

function LoginScreen({ onLogin }) {
  const [userId, setUserId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPass, setAdminPass] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [loginPending, setLoginPending] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [hasScanned, setHasScanned] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerRunningRef = useRef(false);
  const cameraTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const normalImage = '/images/KkunDkun.gif';
  const altImage = '/images/KkunDkun-look.gif';
  const [currentImage, setCurrentImage] = useState(normalImage);
  const [imageLoaded, setImageLoaded] = useState(false);

  const [userList, setUserList] = useState([]);

// ----- ユーザーリストの読み込み ----------------------------------------------------

  useEffect(() => {
    fetch("https://newells4610.stars.ne.jp/data/userList.json", { cache: "no-store" })
      .then(res => res.json())
      .then(data => setUserList(data))
      .catch(err => {
        console.error("ユーザーリスト取得エラー", err);
        setUserList([]);
      });
  }, []);

// ----- K君D君画像の表示 ----------------------------------------------------------

  useEffect(() => {
    // ランダム自動切り替え(ときどきK君こっち見る)
    const interval = setInterval(() => {
      if (Math.random() < 0.1) {
        setCurrentImage(altImage);
        setTimeout(() => setCurrentImage(normalImage), 5000);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {

    // Qキー押したらトグル切り替えでK君ガン見。「こっちみんな。」
    const handleKeyDown = (e) => {
      if (e.key === 'q' || e.key === 'Q') {
        setCurrentImage(prev =>
          prev === normalImage ? altImage : normalImage
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

// ----- 共通：スキャナ停止処理 ---------------------------------------------------

  const stopScanner = async () => {
    if (html5QrCodeRef.current && scannerRunningRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.warn("停止エラー:", err);
      }
      scannerRunningRef.current = false;
      setScanning(false);
    }
    if (cameraTimeoutRef.current) {
      clearTimeout(cameraTimeoutRef.current);
    }
    // インスタンスをリセット
    html5QrCodeRef.current = null;
    // スキャン状態を初期化
    setHasScanned(false);
  };

// ----- QR読み取りの処理 ---------------------------------------------------------

  useEffect(() => {
    const handleResize = () => {
      const isPortraitMode = window.innerHeight > window.innerWidth;
      setIsPortrait(isPortraitMode);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      stopScanner();
    };
  }, []);

  const startScan = () => {
    const qrRegionId = 'reader';
    const html5QrCode = new Html5Qrcode(qrRegionId);
    html5QrCodeRef.current = html5QrCode;
    scannerRunningRef.current = false;
    setHasScanned(false);
    setError('');
    setMessage('');
    setShowWelcome(false);
    setLoginPending(false);

    Html5Qrcode.getCameras().then(devices => {
      if (devices && devices.length) {
        html5QrCode.start(
          { facingMode: 'user' },
          {
            fps: 10,
            qrbox: { width: 200, height: 200 },
            formatsToSupport: ['QR_CODE']
          },
          async (decodedText) => {
            if (!hasScanned) {
              setHasScanned(true);
              handleLogin(decodedText);
            }
          },
          (errorMessage) => {
            // 読み取りエラーは無視してスルー
          }
        ).then(() => {
          scannerRunningRef.current = true;
          setScanning(true);

          cameraTimeoutRef.current = setTimeout(() => {
            stopScanner().then(() => {
              setError('カメラを自動停止しました');
            });
          }, 10000);
        });
      } else {
        setError('カメラが見つかりませんでした');
      }
    }).catch(() => {
      setError('カメラのアクセス許可が必要です');
    });
  };

// ----- ログイン処理 ----------------------------------------------------------------

  const handleLogin = (id = userId.trim()) => {

    if (userList.length === 0) {
      stopScanner();
      setError("ユーザーリストを読み込み中です。数秒後にもう一度お試しください。");
      return;
    }

    if (!id) {
      stopScanner();
      setError('ユーザーIDを入力してください');
      return;
    }

    if (isAdmin && adminPass !== '03811201') {
      stopScanner();
      setError('管理者パスワードが間違っています');
      return;
    }

    // IDがリストにない（未登録なIDをかざした）
    const user = userList.find(u => u.id === id);
    if (!user || !user.name || user.name.trim() === '' || user.name.trim() === '未設定') {
      stopScanner();
      setError('このIDではログインできません。');
      return;
    }

    // 仮に初期値：false
    let allowAdmin = false;

    // 管理者チェックあり(偽装ログインのチェック)
    if (isAdmin) {
      if (adminPass === '03811201') {
        if (user.role === '管理者' || user.role === '発注のみ') {
          allowAdmin = true;
        } else {
          // 利用者なのに管理者ログインを試みた場合 → ログだけ残して一般ログイン

          // サーバーに偽装ログイン履歴を書き出す
          fetch("https://newells4610.stars.ne.jp/api/update_adminlog.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              entries: [
                {
                  datetime: new Date().toISOString(),
                  userId: user.id,
                  userName: user.name,
                  message: "利用者が管理者ログイン試行"
                }
              ]
            }),
          }).catch(err => {
            console.error("偽装ログイン記録エラー:", err);
          });

        }
      } else {
        stopScanner();
        setError('管理者パスワードが間違っています');
        return;
      }
    }

    // 通常ログイン or 認証通過の偽装（対利用者）
    setError('');
    setMessage(`認証しました！${user.name}さんですね！`);
    setShowWelcome(true);
    setLoginPending(true);

    setTimeout(() => {
      onLogin({
        userId: user.id,
        isAdmin: allowAdmin,  // ← 実際の区分を適用
        name: user.name,
        company: user.company,
        role: user.role
      });

      // サーバーにセッションを送信
      (async () => {
        try {
          const res = await fetch("https://newells4610.stars.ne.jp/api/set_session.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: user.id,
              isAdmin: allowAdmin
            })
          });
          const data = await res.json();
          console.log("サーバーセッション設定:", data);
        } catch (err) {
          console.error("セッション送信エラー:", err);
        }
      })();

      navigate('/inventory');
    }, 2000);
  };

  // ------ 画面は横向きでね！ --------------------------------------------------------

  if (isPortrait) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.5em', fontWeight: 'bold', backgroundColor: '#fff0f0', textAlign: 'center', padding: '1em' }}>
        横向きにしてご利用ください📱
      </div>
    );
  }

  if (showWelcome && loginPending) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.5em', fontWeight: 'bold', backgroundColor: '#f0f0f0', textAlign: 'center', padding: '1em' }}>
        {message}
      </div>
    );
  }

  // ------ 画面 ---------------------------------------------------------------------

  return (
    <>
      <div className="login-container" style={{ padding: '0', margin: '0' }}>
        <div style={{ textAlign: 'center', marginBottom: '1em', marginTop: '0' }}>
          <h1 style={{ margin: '0.2em 0' }}>
            <span style={{ fontSize: '1em' }}>KD在庫管理システム[375]</span>
            <span style={{ fontSize: '0.7em' }}>  - ログイン</span>
          </h1>
          <p>
            QRコードをかざしてください。またはIDを入力してください。
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '2em', flexWrap: 'wrap' }}>
          {/* カメラ領域とボタン */}
          <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
            {!scanning && (
              <div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'center' }}>
                <button onClick={startScan}>QR読み取り開始</button>
              </div>
            )}
            {error && 
              <div className="error-message" style={{ color: 'red', marginBottom: '1em', display: 'flex', justifyContent: 'center' }}>{error}</div>}
            <div
              id="reader"
              style={{
                width: '100%',
                height: '300px',
                marginBottom: '0.5em',
                visibility: scanning ? 'visible' : 'hidden',
                border: '1px solid #ccc',
                position: 'relative'
              }}
            ></div>
          </div>

          {/* 入力フォーム */}
          <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="ユーザーID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              style={{ width: '100%', marginBottom: '0.5em' }}
            />
            <div className="admin-check" style={{ marginTop: '0.5em', textAlign: 'left' }}>
              <label>
                <input
                  type="checkbox"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                /> 管理者としてログイン
              </label>
            </div>
            {isAdmin && (
              <input
                type="password"
                placeholder="パスワード（数字8桁）"
                value={adminPass}
                onChange={(e) => setAdminPass(e.target.value)}
                style={{ width: '100%', marginTop: '0.5em' }}
              />
            )}
            <button onClick={() => handleLogin()} style={{ marginTop: '0.8em' }}>ログイン</button>
          </div>
        </div>
      </div>

      {/* Kくんの画像 */}
      <img
        src={currentImage}
        alt="KとDキャラ"
        className="login-character"
        onLoad={() => setImageLoaded(true)}
      />
    </>
  );
}

export default LoginScreen;
